
import { useState, useEffect, useRef, useCallback } from "react";
import "./SidescrollAdvertisements.css";
import { FaHeart, FaRegHeart, FaComment, FaShare } from "react-icons/fa";
import { 
  FaCompass, 
  FaUtensils, 
  FaBirthdayCake, 
  FaTruck, 
  FaShoppingCart 
} from "react-icons/fa";

const BASE_URL = "http://testing.maamaas.com:8080";
 
const ReelsSection = () => {
    const reelsContainerRef = useRef(null);
    const autoScrollIntervalRef = useRef(null);
    const [isAutoScrolling, setIsAutoScrolling] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);

    const [campaigns, setCampaigns] = useState([]);
    const [filteredCampaigns, setFilteredCampaigns] = useState([]);
    const [activeCategory, setActiveCategory] = useState("all");
    const [isLiking, setIsLiking] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);
    const [isInitialized, setIsInitialized] = useState(false);

    // =====================
    // AUTO SCROLL FUNCTION
    // =====================
    const scrollToNext = useCallback(() => {
        if (!reelsContainerRef.current || !filteredCampaigns.length) return;
        
        const container = reelsContainerRef.current;
        const reelHeight = container.children[0]?.offsetHeight || window.innerHeight;
        const currentScroll = container.scrollTop;
        const maxScroll = container.scrollHeight - container.clientHeight;
        
        let nextScroll = currentScroll + reelHeight;
        if (nextScroll >= maxScroll) {
            nextScroll = 0; // Loop back to start
            setCurrentIndex(0);
        } else {
            setCurrentIndex(prev => prev + 1);
        }
        
        container.scrollTo({
            top: nextScroll,
            behavior: 'smooth'
        });
    }, [filteredCampaigns.length]);

    const startAutoScroll = useCallback(() => {
        if (autoScrollIntervalRef.current) clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = setInterval(() => {
            if (isAutoScrolling && filteredCampaigns.length > 0) {
                scrollToNext();
            }
        }, 3000); // Scroll every 3 seconds
    }, [isAutoScrolling, filteredCampaigns.length, scrollToNext]);

    const stopAutoScroll = useCallback(() => {
        if (autoScrollIntervalRef.current) {
            clearInterval(autoScrollIntervalRef.current);
            autoScrollIntervalRef.current = null;
        }
    }, []);

    // Handle manual scroll - stop auto scroll temporarily
    const handleManualScroll = useCallback(() => {
        setIsAutoScrolling(false);
        stopAutoScroll();
        
        // Restart auto scroll after 5 seconds of inactivity
        setTimeout(() => {
            if (!autoScrollIntervalRef.current) {
                setIsAutoScrolling(true);
                startAutoScroll();
            }
        }, 5000);
    }, [stopAutoScroll, startAutoScroll]);

    // Update current index on scroll
    const handleScroll = useCallback(() => {
        if (!reelsContainerRef.current || !filteredCampaigns.length) return;
        
        const container = reelsContainerRef.current;
        const reelHeight = container.children[0]?.offsetHeight || window.innerHeight;
        const scrollPosition = container.scrollTop;
        const newIndex = Math.round(scrollPosition / reelHeight);
        
        if (newIndex !== currentIndex && newIndex >= 0 && newIndex < filteredCampaigns.length) {
            setCurrentIndex(newIndex);
        }
    }, [filteredCampaigns.length, currentIndex]);

    // =====================
    // AUTH HELPERS
    // =====================
    const getAuthData = () => {
        const token = localStorage.getItem("authToken");
        const userId = localStorage.getItem("userId");
        const customerId = localStorage.getItem("customerId") || "usrUSR140520263";
        const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";
        return { token, userId, customerId, isLoggedIn };
    };

    // =====================
    // LOCATION HELPERS
    // =====================
    const getCurrentLocationFromStorage = () => {
        const { userId } = getAuthData();
        if (!userId) return null;
        const currentKey = `currentLocation_${userId}`;
        let raw = localStorage.getItem(currentKey);
        if (!raw) raw = localStorage.getItem("currentLocation");
        if (!raw) return null;
        try { return JSON.parse(raw); } catch { return null; }
    };

    const getSelectedAddressFromStorage = () => {
        const { userId } = getAuthData();
        if (!userId) return null;
        const selectedKey = `selectedAddress_${userId}`;
        let raw = localStorage.getItem(selectedKey);
        if (!raw) raw = localStorage.getItem("selectedAddress");
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object") {
                if (parsed.full) return parsed.full;
                if (parsed.address) return parsed.address;
                if (parsed.lat && parsed.lng) return "Selected Location";
                return JSON.stringify(parsed);
            }
            return parsed;
        } catch { return raw; }
    };

    const hasLocationInStorage = () => {
        const location = getCurrentLocationFromStorage();
        const address = getSelectedAddressFromStorage();
        const hasValidLocation = location && (location.lat || location.latitude) && (location.lng || location.longitude);
        const hasValidAddress = address && address.length > 0;
        return hasValidLocation && hasValidAddress;
    };

    // =====================
    // INTERESTS
    // =====================
    const interests = [
    { id: "all", label: "All", icon: <FaCompass /> },
    { id: "FOOD_AND_BEVERAGES", label: "Food", icon: <FaUtensils /> },
    { id: "CATERING", label: "Catering", icon: <FaBirthdayCake /> },
    { id: "LOGISTICS", label: "Logistics", icon: <FaTruck /> },
    { id: "GROCERY", label: "Grocery", icon: <FaShoppingCart /> },
];

    // =====================
    // SAVE LOCATION API
    // =====================
    const createCustomerLocation = async () => {
        const { token, userId, customerId } = getAuthData();
        if (!token || !userId) return false;
        if (!token.startsWith("eyJ")) return false;
        const location = getCurrentLocationFromStorage();
        const address = getSelectedAddressFromStorage();
        if (!location || !address) return false;
        const requestBody = {
            userId: Number(userId),
            customerId,
            latitude: location.lat || location.latitude || 17.4893,
            longitude: location.lng || location.longitude || 78.3927,
            address,
            city: location.city || "",
            category: "default",
        };
        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 8000);
            const response = await fetch(`${BASE_URL}/subscription/api/user/current/location/update`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(requestBody),
                signal: controller.signal,
            });
            clearTimeout(timeout);
            return response.ok;
        } catch { return false; }
    };

    // =====================
    // TRACK VIEWS
    // =====================
    const trackCampaignViews = (campaignsArray) => {
        const { token, customerId } = getAuthData();
        if (!token || !customerId || campaignsArray.length === 0) return;
        campaignsArray.forEach((campaign) => {
            fetch(`${BASE_URL}/promotions/api/user/campaign/view`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ campaignId: campaign.id, customerId }),
            }).catch(() => { });
        });
    };

    // =====================
    // PROCESS CAMPAIGNS
    // =====================
    const processCampaignsData = (data) => {
        let raw = [];
        if (Array.isArray(data)) raw = data;
        else if (Array.isArray(data?.data)) raw = data.data;
        else if (Array.isArray(data?.content)) raw = data.content;
        else if (Array.isArray(data?.campaigns)) raw = data.campaigns;

        if (raw.length === 0) {
            setError("NO_CAMPAIGNS");
            setCampaigns([]);
            setFilteredCampaigns([]);
            return;
        }

        const processed = raw.map((c) => ({
            ...c,
            id: c.id || c.campaignId,
            likedByCurrentUser: c.likedByCurrentUser || false,
            likesCount: c.likesCount || 0,
            sharesCount: c.sharesCount || 0,
            viewsCount: c.viewsCount || 0,
        }));

        setCampaigns(processed);
        setFilteredCampaigns(processed);
        setError(null);
        trackCampaignViews(processed);
        
        // Reset index when new data loads
        setCurrentIndex(0);
        if (reelsContainerRef.current) {
            reelsContainerRef.current.scrollTop = 0;
        }
    };

    // =====================
    // FETCH CAMPAIGNS
    // =====================
    const fetchCampaigns = useCallback(async () => {
        setLoading(true);
        setError(null);
        const { token, userId, customerId, isLoggedIn } = getAuthData();

        if (!isLoggedIn || !token || !userId) {
            setError("PLEASE_LOGIN");
            setLoading(false);
            return;
        }

        if (!hasLocationInStorage()) {
            setError("LOCATION_REQUIRED");
            setLoading(false);
            return;
        }

        try { await createCustomerLocation(); } catch { }

        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 15000);
            const apiUrl = `${BASE_URL}/promotions/api/user/active/campaigns?customerId=${customerId}&userId=${userId}`;
            const response = await fetch(apiUrl, {
                method: "GET",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                signal: controller.signal,
            });
            clearTimeout(timeout);

            if (response.status === 401 || response.status === 403) {
                localStorage.removeItem("authToken");
                localStorage.removeItem("isLoggedIn");
                setError("PLEASE_LOGIN");
                setCampaigns([]);
                setFilteredCampaigns([]);
                return;
            }
            if (!response.ok) {
                setError("NO_CAMPAIGNS");
                setCampaigns([]);
                setFilteredCampaigns([]);
                return;
            }

            const data = await response.json();
            if (!data || (Array.isArray(data) && data.length === 0)) {
                setError("NO_CAMPAIGNS");
                setCampaigns([]);
                setFilteredCampaigns([]);
                return;
            }
            processCampaignsData(data);
        } catch (err) {
            setError(err.name === "AbortError" ? "TIMEOUT" : "SERVER_ERROR");
            setCampaigns([]);
            setFilteredCampaigns([]);
        } finally {
            setLoading(false);
        }
    }, [retryCount]);

    // =====================
    // LIKE API
    // =====================
    const handleLike = async (campaignId, currentlyLiked) => {
        const { token, customerId, isLoggedIn } = getAuthData();
        if (!isLoggedIn || !token) { alert("Please login to like campaigns"); return; }
        if (isLiking) return;

        const applyToggle = (list) =>
            list.map((c) =>
                c.id === campaignId
                    ? { ...c, likedByCurrentUser: !currentlyLiked, likesCount: currentlyLiked ? c.likesCount - 1 : c.likesCount + 1 }
                    : c
            );

        setCampaigns((prev) => applyToggle(prev));
        setFilteredCampaigns((prev) => applyToggle(prev));
        setIsLiking(true);

        try {
            const response = await fetch(`${BASE_URL}/promotions/api/user/campaign/like`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ campaignId, customerId, interactionType: "LIKE" }),
            });
            if (!response.ok) {
                const revert = (list) =>
                    list.map((c) =>
                        c.id === campaignId
                            ? { ...c, likedByCurrentUser: currentlyLiked, likesCount: currentlyLiked ? c.likesCount + 1 : c.likesCount - 1 }
                            : c
                    );
                setCampaigns((prev) => revert(prev));
                setFilteredCampaigns((prev) => revert(prev));
            }
        } catch {
            const revert = (list) =>
                list.map((c) =>
                    c.id === campaignId
                        ? { ...c, likedByCurrentUser: currentlyLiked, likesCount: currentlyLiked ? c.likesCount + 1 : c.likesCount - 1 }
                        : c
                );
            setCampaigns((prev) => revert(prev));
            setFilteredCampaigns((prev) => revert(prev));
        } finally {
            setIsLiking(false);
        }
    };

    // =====================
    // SHARE API
    // =====================
    const handleShare = async (campaign) => {
        const { token, customerId, isLoggedIn } = getAuthData();
        if (!isLoggedIn) { alert("Please login to share campaigns"); return; }

        try {
            if (navigator.share) {
                await navigator.share({
                    title: campaign.campaignName,
                    text: campaign.description || `Check out ${campaign.campaignName}`,
                    url: window.location.href,
                });
            } else {
                await navigator.clipboard.writeText(
                    `${campaign.campaignName}\n${campaign.description || ""}\n${window.location.href}`
                );
                alert("Link copied to clipboard!");
            }

            if (token && customerId) {
                fetch(`${BASE_URL}/promotions/api/user/campaign/share`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ campaignId: campaign.id, customerId }),
                })
                    .then((res) => {
                        if (res.ok) {
                            setFilteredCampaigns((prev) =>
                                prev.map((c) =>
                                    c.id === campaign.id ? { ...c, sharesCount: (c.sharesCount || 0) + 1 } : c
                                )
                            );
                        }
                    })
                    .catch(() => { });
            }
        } catch (err) {
            if (err.name !== "AbortError") console.error("Share error:", err);
        }
    };

    // =====================
    // CATEGORY FILTER
    // =====================
    const handleCategoryChange = (categoryId) => {
        setActiveCategory(categoryId);
        const filtered = categoryId === "all" ? campaigns : campaigns.filter((c) => c.appType === categoryId);
        setFilteredCampaigns(filtered);
        setCurrentIndex(0);
        if (reelsContainerRef.current) {
            reelsContainerRef.current.scrollTop = 0;
        }
        // Restart auto scroll
        stopAutoScroll();
        setIsAutoScrolling(true);
        startAutoScroll();
    };

    // =====================
    // EFFECTS
    // =====================
    useEffect(() => {
        const initializeComponent = async () => {
            const { isLoggedIn } = getAuthData();
            if (isLoggedIn) {
                if (hasLocationInStorage()) {
                    await fetchCampaigns();
                } else {
                    setError("LOCATION_REQUIRED");
                    setLoading(false);
                }
            } else {
                setError("PLEASE_LOGIN");
                setLoading(false);
            }
            setIsInitialized(true);
        };
        initializeComponent();
    }, []);

    useEffect(() => {
        if (isInitialized) fetchCampaigns();
    }, [retryCount, isInitialized]);

    useEffect(() => {
        const handleLocationChange = () => setRetryCount((prev) => prev + 1);
        window.addEventListener("storage", handleLocationChange);
        window.addEventListener("locationUpdated", handleLocationChange);
        return () => {
            window.removeEventListener("storage", handleLocationChange);
            window.removeEventListener("locationUpdated", handleLocationChange);
        };
    }, []);

    // Auto scroll effect
    useEffect(() => {
        if (filteredCampaigns.length > 0 && !loading && !error) {
            startAutoScroll();
        }
        return () => stopAutoScroll();
    }, [filteredCampaigns.length, loading, error, startAutoScroll, stopAutoScroll]);

    // Add scroll event listener
    useEffect(() => {
        const container = reelsContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            container.addEventListener('wheel', handleManualScroll);
            container.addEventListener('touchstart', handleManualScroll);
            return () => {
                container.removeEventListener('scroll', handleScroll);
                container.removeEventListener('wheel', handleManualScroll);
                container.removeEventListener('touchstart', handleManualScroll);
            };
        }
    }, [handleScroll, handleManualScroll]);

    // =====================
    // HELPERS
    // =====================
    const formatDate = (dateString) => {
        if (!dateString) return "";
        return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric" });
    };

    const handleRetry = () => setRetryCount((prev) => prev + 1);
    const { isLoggedIn } = getAuthData();

    // =====================
    // ERROR STATE
    // =====================
    const renderErrorState = () => (
        <div className="no-campaigns">
            <p>No Campaigns Available</p>
        </div>
    );

    // =====================
    // RENDER
    // =====================
    return (
        <div className="reels-section">
            {/* CATEGORY TABS */}
            {isLoggedIn && error !== "LOCATION_REQUIRED" && (
                <div className="interests-tabs">
                    {interests.map((cat) => (
                        <button
                            key={cat.id}
                            className={`category-tab ${activeCategory === cat.id ? "active" : ""}`}
                            onClick={() => handleCategoryChange(cat.id)}
                        >
                            <span className="material-icons-round">{cat.icon}</span>
                            <span className="tab-label">{cat.label}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* REELS CONTAINER */}
            <div className="reels-container" ref={reelsContainerRef}>

                {loading && (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading campaigns...</p>
                    </div>
                )}

                {!loading && error && renderErrorState()}

                {!loading && !error && filteredCampaigns.length === 0 && renderErrorState()}

                {!loading && !error && filteredCampaigns.map((item, idx) => (
                    <div key={item.id} className="reel-item">
                        <div className="ad-container">

                            {/* Campaign Image */}
                            <img
                                src={item.imageUrl}
                                alt={item.campaignName}
                                className="ad-images"
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "https://via.placeholder.com/400x600?text=No+Image";
                                }}
                            />

                            {/* Dark gradient overlay for text readability */}
                            <div className="overlay-gradient" />

                            {/* Brand Info - Bottom Left */}
                            <div className="ad-brand-info">
                                <div className="ad-brand-details">
                                    <span className="ad-brand-name">{item.campaignName}</span>
                                    {item.description && (
                                        <span className="ad-product-name">{item.description}</span>
                                    )}
                                    {(item.startDate || item.endDate) && (
                                        <span className="ad-dates">
                                            {formatDate(item.startDate)} – {formatDate(item.endDate)}
                                        </span>
                                    )}
                                    {item.discountPercentage > 0 && (
                                        <span className="ad-discount">
                                            🔥 {item.discountPercentage}% OFF
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* RIGHT SIDE ACTIONS */}
                            <div className="reel-actions">

                                {/* Like Button */}
                                <div
                                    className="action-item"
                                    onClick={() => !isLiking && handleLike(item.id, item.likedByCurrentUser)}
                                    style={{
                                        cursor: isLiking ? "not-allowed" : "pointer",
                                        opacity: isLiking ? 0.7 : 1
                                    }}
                                >
                                    <div className={`action-icon-wrap ${item.likedByCurrentUser ? "liked" : ""}`}>
                                        {item.likedByCurrentUser ? (
                                            <FaHeart size={22} color="#ff3b30" />
                                        ) : (
                                            <FaRegHeart size={22} color="white" />
                                        )}
                                    </div>

                                    <span className="action-count">
                                        {item.likesCount > 0 ? item.likesCount : ""}
                                    </span>
                                </div>

                                {/* Share Button */}
                                <div
                                    className="action-item"
                                    onClick={() => handleShare(item)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="action-icon-wrap">
                                        <FaShare size={22} color="white" />
                                    </div>

                                    <span className="action-count">
                                        {item.sharesCount > 0 ? item.sharesCount : ""}
                                    </span>
                                </div>

                            </div>

                        </div>
                        {/* Optional: Progress indicator for auto-scroll */}
                        {idx === currentIndex && isAutoScrolling && (
                            <div style={{
                                position: "absolute",
                                bottom: "10px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: "60px",
                                height: "3px",
                                backgroundColor: "rgba(255,255,255,0.3)",
                                borderRadius: "2px",
                                overflow: "hidden"
                            }}>
                                <div style={{
                                    width: "100%",
                                    height: "100%",
                                    backgroundColor: "#ee6521",
                                    animation: "progressBar 3s linear infinite"
                                }} />
                            </div>
                        )}
                    </div>
                ))}

            </div>
            <style>
                {`
                    @keyframes progressBar {
                        0% { width: 0%; }
                        100% { width: 100%; }
                    }
                `}
            </style>
        </div>
    );
};

export default ReelsSection;